/**
 * PATH: packages/ui/src/components/global-search/extraProviders.ts
 * FILE_ID: UI-GLOBALSEARCH-EXTRA-PROVIDERS-001
 * PURPOSE: Live registry so a mounted workbook can feed Cmd+K without
 *          mounting packages/ui CommandPalette.tsx.
 * WHY: DashboardLayout already owns GlobalSearch. Quote-builder registers
 *      a workbook provider + daily-verb actions on mount and drops them
 *      on unmount. useSyncExternalStore so the layout re-merges.
 */
import { useSyncExternalStore } from 'react'

import type { QuickAction, SearchProvider } from './types'

type ExtraSearchState = {
  providers: SearchProvider[]
  actions: QuickAction[]
}

let state: ExtraSearchState = { providers: [], actions: [] }
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((fn) => fn())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): ExtraSearchState {
  return state
}

function setState(next: ExtraSearchState) {
  state = next
  emit()
}

export function registerExtraSearchProvider(provider: SearchProvider): () => void {
  setState({ ...state, providers: [...state.providers.filter((p) => p.id !== provider.id), provider] })
  return () => {
    setState({ ...state, providers: state.providers.filter((p) => p.id !== provider.id) })
  }
}

export function registerExtraQuickActions(actions: QuickAction[]): () => void {
  const ids = new Set(actions.map((a) => a.id))
  setState({
    ...state,
    actions: [...state.actions.filter((a) => !ids.has(a.id)), ...actions],
  })
  return () => {
    setState({ ...state, actions: state.actions.filter((a) => !ids.has(a.id)) })
  }
}

export function useExtraGlobalSearch(): ExtraSearchState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function __resetExtraGlobalSearch() {
  state = { providers: [], actions: [] }
  emit()
}
